import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels, carDetailsLabels } from "@/i18n";

interface TireSectionProps {
  currentTireType: string | undefined;
  currentRimType: string | undefined;
  extraTireSet: boolean | undefined;
  extraTireType: string | undefined;
  extraRimType: string | undefined;
  onSelect: (
    value: string | boolean,
    field:
      | "currentTireType"
      | "currentRimType"
      | "extraTireSet"
      | "extraTireType"
      | "extraRimType",
  ) => void;
}

const TIRE_TYPE_KEYS = [
  "Sommar",
  "Vinter (dubbade)",
  "Vinter (dubbfria)",
  "Helårsdäck",
];
const RIM_TYPE_KEYS = ["Originalfälgar", "Aluminium", "Plåt"];

export const TireSection = ({
  currentTireType,
  currentRimType,
  extraTireSet,
  extraTireType,
  extraRimType,
  onSelect,
}: TireSectionProps) => {
  const { language } = useLanguage();
  const vLabels = valuationLabels(language).vehicleInfo;
  const w = carDetailsLabels(language).wheels;
  const tireTypes = (w.tireTypes as Record<string, string>) || {};
  const rimTypes = (w.rimTypes as Record<string, string>) || {};
  const tireOptions = TIRE_TYPE_KEYS.map((v) => ({
    value: v,
    label: tireTypes[v] ?? v,
  }));
  const rimOptions = RIM_TYPE_KEYS.map((v) => ({
    value: v,
    label: rimTypes[v] ?? v,
  }));
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="font-medium text-sm">{vLabels.currentTiresTitle}</h3>
        <div className="flex flex-wrap gap-2">
          {tireOptions.map((t) => (
            <Button
              key={t.value}
              type="button"
              variant="formOption"
              className={
                currentTireType === t.value
                  ? "border-accent border-2 bg-accent/20"
                  : "border-gray-400"
              }
              onClick={() => onSelect(t.value, "currentTireType")}
            >
              {t.label}
            </Button>
          ))}
        </div>
      </div>

      {currentTireType && (
        <div className="space-y-2">
          <h4 className="font-medium text-sm">{vLabels.rimTypeTitle}</h4>
          <div className="flex flex-wrap gap-2">
            {rimOptions.map((r) => (
              <Button
                key={r.value}
                type="button"
                variant="formOption"
                className={
                  currentRimType === r.value
                    ? "border-accent border-2 bg-accent/20"
                    : "border-gray-400"
                }
                onClick={() => onSelect(r.value, "currentRimType")}
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 py-3 px-4 border-2 border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-800">
        <Checkbox
          id="extra-tire-set"
          checked={!!extraTireSet}
          onCheckedChange={(checked) => {
            onSelect(checked, "extraTireSet");
            if (!checked) {
              onSelect("", "extraTireType");
              onSelect("", "extraRimType");
            }
          }}
          className="h-5 w-5"
        />
        <label
          htmlFor="extra-tire-set"
          className="text-base font-medium cursor-pointer"
        >
          {vLabels.extraTireSetLabel}
        </label>
      </div>

      {extraTireSet && (
        <div className="space-y-4 pl-4 border-l-2 border-gray-200">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">
              {vLabels.extraTireSetTypeTitle}
            </h4>
            <div className="flex flex-wrap gap-2">
              {tireOptions.map((t) => (
                <Button
                  key={t.value}
                  type="button"
                  variant="formOption"
                  className={
                    extraTireType === t.value
                      ? "border-accent border-2"
                      : "border-gray-400"
                  }
                  onClick={() => onSelect(t.value, "extraTireType")}
                >
                  {t.label}
                </Button>
              ))}
            </div>
          </div>

          {extraTireType && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm">{vLabels.rimTypeTitle}</h4>
              <div className="flex flex-wrap gap-2">
                {rimOptions.map((r) => (
                  <Button
                    key={r.value}
                    type="button"
                    variant="formOption"
                    className={
                      extraRimType === r.value
                        ? "border-accent border-2 bg-accent/20"
                        : "border-gray-400"
                    }
                    onClick={() => onSelect(r.value, "extraRimType")}
                  >
                    {r.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
